import { useState } from 'react';
import { Stack, Paper, TextInput, Group, Select, Text, Skeleton, Badge, Anchor } from '@mantine/core';
import { IconSearch, IconExternalLink } from '@tabler/icons-react';
import { DataTable, type DataTableSortStatus } from 'mantine-datatable';
import { useGoogleAdsAds, useGoogleAdsAccount } from '@/hooks/useGoogleAds';
import { microsToAmount, formatPercentage } from '@/types/googleAds';
import type { DateRangeOption, Ad } from '@/types/googleAds';
import { StatusBadge } from '../components/StatusBadge';

interface AdsTabProps {
  customerId: string;
  dateRange: DateRangeOption;
}

export function AdsTab({ customerId }: AdsTabProps) {
  const { data: ads, isLoading } = useGoogleAdsAds(customerId);
  const { data: account } = useGoogleAdsAccount(customerId);

  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sortStatus, setSortStatus] = useState<DataTableSortStatus<Ad>>({
    columnAccessor: 'metrics.costMicros',
    direction: 'desc',
  });

  const formatCurrency = (micros: number) =>
    new Intl.NumberFormat('de-DE', { style: 'currency', currency: account?.currency ?? 'EUR' }).format(
      microsToAmount(micros),
    );

  const formatNumber = (num: number) => new Intl.NumberFormat('de-DE').format(num);

  const filteredAds = ads
    ?.filter((ad) => {
      if (search) {
        const searchLower = search.toLowerCase();
        const matchesHeadline = ad.headlines.some((h) => h.text.toLowerCase().includes(searchLower));
        const matchesDescription = ad.descriptions.some((d) => d.text.toLowerCase().includes(searchLower));
        const matchesCampaign = ad.campaignName.toLowerCase().includes(searchLower);
        if (!matchesHeadline && !matchesDescription && !matchesCampaign) return false;
      }
      if (typeFilter && ad.type !== typeFilter) return false;
      if (statusFilter && ad.status !== statusFilter) return false;
      return true;
    })
    .sort((a, b) => {
      const accessor = sortStatus.columnAccessor;
      let aVal: number | string;
      let bVal: number | string;

      if (accessor.startsWith('metrics.')) {
        const key = accessor.replace('metrics.', '') as keyof Ad['metrics'];
        aVal = a.metrics[key];
        bVal = b.metrics[key];
      } else {
        aVal = a[accessor as keyof Ad] as string;
        bVal = b[accessor as keyof Ad] as string;
      }

      if (typeof aVal === 'string') {
        return sortStatus.direction === 'asc' ? aVal.localeCompare(bVal as string) : (bVal as string).localeCompare(aVal);
      }
      return sortStatus.direction === 'asc' ? (aVal as number) - (bVal as number) : (bVal as number) - (aVal as number);
    });

  const uniqueTypes = [...new Set(ads?.map((ad) => ad.type) ?? [])];

  if (isLoading) {
    return (
      <Stack gap="md">
        <Skeleton height={40} />
        <Skeleton height={400} />
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      <Paper withBorder p="md">
        <Group>
          <TextInput
            placeholder="Anzeige suchen..."
            leftSection={<IconSearch size={16} />}
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            w={250}
          />
          <Select
            placeholder="Anzeigentyp"
            data={[
              { value: '', label: 'Alle Typen' },
              ...uniqueTypes.map((t) => ({ value: t, label: t.replace(/_/g, ' ') })),
            ]}
            value={typeFilter}
            onChange={(v) => setTypeFilter(v ?? '')}
            clearable
            w={200}
          />
          <Select
            placeholder="Status"
            data={[
              { value: '', label: 'Alle Status' },
              { value: 'ENABLED', label: 'Aktiv' },
              { value: 'PAUSED', label: 'Pausiert' },
              { value: 'REMOVED', label: 'Entfernt' },
            ]}
            value={statusFilter}
            onChange={(v) => setStatusFilter(v ?? '')}
            clearable
            w={150}
          />
          <Text size="sm" c="dimmed" ml="auto">
            {filteredAds?.length ?? 0} Anzeigen
          </Text>
        </Group>
      </Paper>

      <DataTable
        withTableBorder
        borderRadius="sm"
        striped
        highlightOnHover
        records={filteredAds ?? []}
        sortStatus={sortStatus}
        onSortStatusChange={setSortStatus}
        columns={[
          {
            accessor: 'headlines',
            title: 'Anzeige',
            width: 350,
            render: (ad) => (
              <div>
                <Text size="sm" fw={500} lineClamp={2}>
                  {ad.headlines.map((h) => h.text).join(' | ')}
                </Text>
                <Text size="xs" c="dimmed" lineClamp={1}>
                  {ad.descriptions.map((d) => d.text).join(' ')}
                </Text>
                <Group gap="xs" mt={4}>
                  <Text size="xs" c="dimmed">
                    {ad.campaignName}
                  </Text>
                  <Text size="xs" c="dimmed">
                    /
                  </Text>
                  <Text size="xs" c="dimmed">
                    {ad.adGroupName}
                  </Text>
                </Group>
              </div>
            ),
          },
          {
            accessor: 'type',
            title: 'Typ',
            width: 150,
            render: (ad) => (
              <Badge variant="light" size="sm">
                {ad.type.replace(/_/g, ' ')}
              </Badge>
            ),
          },
          {
            accessor: 'status',
            title: 'Status',
            width: 90,
            render: (ad) => <StatusBadge status={ad.status} />,
          },
          {
            accessor: 'finalUrls',
            title: 'URL',
            width: 200,
            render: (ad) =>
              ad.finalUrls.length > 0 ? (
                <Anchor
                  href={ad.finalUrls[0]}
                  target="_blank"
                  rel="noopener noreferrer"
                  size="xs"
                  lineClamp={1}
                >
                  {(() => { try { const url = ad.finalUrls[0]; return url ? new URL(url).hostname : '-'; } catch { return ad.finalUrls[0] ?? '-'; } })()}
                  <IconExternalLink size={12} style={{ marginLeft: 4 }} />
                </Anchor>
              ) : (
                '-'
              ),
          },
          {
            accessor: 'metrics.impressions',
            title: 'Impr.',
            sortable: true,
            textAlign: 'right',
            width: 90,
            render: (ad) => formatNumber(ad.metrics.impressions),
          },
          {
            accessor: 'metrics.clicks',
            title: 'Klicks',
            sortable: true,
            textAlign: 'right',
            width: 70,
            render: (ad) => formatNumber(ad.metrics.clicks),
          },
          {
            accessor: 'metrics.ctr',
            title: 'CTR',
            sortable: true,
            textAlign: 'right',
            width: 70,
            render: (ad) => formatPercentage(ad.metrics.ctr),
          },
          {
            accessor: 'metrics.costMicros',
            title: 'Kosten',
            sortable: true,
            textAlign: 'right',
            width: 90,
            render: (ad) => formatCurrency(ad.metrics.costMicros),
          },
          {
            accessor: 'metrics.conversions',
            title: 'Conv.',
            sortable: true,
            textAlign: 'right',
            width: 70,
            render: (ad) => ad.metrics.conversions.toFixed(1),
          },
          {
            accessor: 'metrics.conversionRate',
            title: 'Conv. Rate',
            sortable: true,
            textAlign: 'right',
            width: 90,
            render: (ad) => formatPercentage(ad.metrics.conversionRate),
          },
        ]}
        noRecordsText="Keine Anzeigen gefunden"
        minHeight={400}
      />
    </Stack>
  );
}
